import { useRef, RefObject } from "react"
import { FormikProps } from "formik"
import { enqueueSnackbar } from "notistack"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { createMessage, MerchantMessagesQuery } from "../messagesApi"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { selectUserId } from "../../../../login/loginSelectors"
import { useAppSelector } from "../../../../store/storeHooks"
import { UserType } from "../../../../login/loginTypes"
import {
    CreateEditMessageFormValues,
    MessageAdditionalData,
    MessageAdditionalDataRecipientsType,
    MessageItem,
} from "../messagesTypes"

type UseCreateMessageFlowResult = {
    formRef: RefObject<FormikProps<CreateEditMessageFormValues>>
    handleSaveClick: () => void
    handleSubmit: (data: MessageItem) => void
}

export function useCreateMessageFlow(onAfterSuccess?: () => void): UseCreateMessageFlowResult {
    const { merchantId } = useMerchant()
    const userId = useAppSelector(selectUserId)

    const formRef = useRef<FormikProps<CreateEditMessageFormValues>>(null)

    const messageAdditionalData: MessageAdditionalData = {
        sender: {
            type: UserType.MERCHANT,
            merchant: {
                id: merchantId,
            },
            user: {
                id: userId || 0,
            },
        },
        recipients: [
            {
                type: MessageAdditionalDataRecipientsType.MERCHANT_CUSTOMERS,
                userTypes: [UserType.USER],
            },
        ],
    }

    const { mutateAsync: callCreateMessage } = useApiMutation(createMessage, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantMessagesQuery.LIST] })
            onAfterSuccess?.()

            if (!data.serviceResponse.displayMessage) return
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    const handleSubmit = (data: MessageItem) => {
        void callCreateMessage({
            message: data,
            messageAdditionalData,
        })
    }

    return {
        formRef,
        handleSaveClick,
        handleSubmit,
    }
}
